import State from './State'
import Service from './Service'
import Customer from './Customer'
import { AuditorRequest } from './Auditor'
import { Reviewer } from './Reviewer'
import Profile from '@/classes/auth/Profile'
import ProfileType from '@/classes/auth/ProfileType'
import { Certificate } from '@/classes/certificate/Certificate'
import { InvalidStateError } from '../errors'
import { dateTimeFormat } from '@/utility/utility_functions'

class Case {
  static ACCEPT_CASE = 1
  static REJECT_CASE = 2
  static BEGIN_REVIEW = 3
  static CONTINUE_REVIEW = 4
  static REQUEST_AUDIOR = 5
  static CLEAR_RESPONSIBLE = 6
  static CLAIM_CASE = 7
  static SET_INVOICED = 8
  static CASE_TYPE_REVISION = 1
  static CASE_TYPE_REPORTING = 2
  static CASE_TYPE_RENEWAL = 3
  static CASE_TYPE_REVISE = 4
  static CASE_TYPE_OVERTAKING = 5

  constructor (id, state, caseNumber, applicationDate, signature, qualification, created, modified, responsible, service, reviewer, customer, progress, certificate, result, auditorRequests, caseType, examSelection, bookingId, examLocation, examDate, controller, invoiced, invoiceIds, listOnBoverket, currentCa, currentCertificateNumber, invoiceable, idCard, memberOfSBR) {
    this.id = id
    this.state = state
    this.caseNumber = caseNumber
    this.applicationDate = applicationDate
    this.signature = signature
    this.qualification = qualification
    this.created = created
    this.modified = modified
    this.responsible = responsible
    this.service = service
    this.reviewer = reviewer
    this.customer = customer
    this.progress = progress
    this.certificate = certificate
    this.result = result
    this.auditorRequests = auditorRequests
    this.caseType = caseType
    this.examSelection = examSelection
    this.bookingId = bookingId
    this.examLocation = examLocation
    this.examDate = examDate
    this.controller = controller
    this.invoiced = invoiced
    this.invoiceIds = invoiceIds
    this.documents = []
    this.listOnBoverket = listOnBoverket
    this.currentCa = currentCa
    this.currentCertificateNumber = currentCertificateNumber
    this.invoiceable = invoiceable
    this.idCard = idCard
    this.memberOfSBR = memberOfSBR
  }

  static asCases (json) {
    return json.map(jsonCase => {
      return Case.asCase(jsonCase)
    })
  }

  static asCase (json) {
    return new Case(
      json.id,
      CaseState.asState(json.state),
      json.case_id,
      json.application.application_date ? dateTimeFormat(json.application.application_date) : '',
      json.application.signature ? Signature.asSignature(json.application.signature) : null,
      json.qualification,
      json.created ? dateTimeFormat(json.created, 'YYYY-MM-DD') : '',
      json.modified ? dateTimeFormat(json.modified, 'YYYY-MM-DD HH:mm') : '',
      json.responsible ? ProfileType.asProfileType(json.responsible) : null,
      json.service ? Service.asService(json.service) : null,
      json.reviewer ? Reviewer.asReviewer(json.reviewer) : null,
      json.customer ? Customer.asCustomer(json.customer) : null,
      json.progress ? { progress: json.progress } : 0,
      json.certificate ? Certificate.asCertificate(json.certificate) : null,
      json.result ? CaseResult.asCaseResult(json.result) : null,
      json.auditor_requests ? AuditorRequest.asAuditorRequests(json.auditor_requests) : [],
      json.case_type ? CaseType.asCaseType(json.case_type) : {},
      json.exam_selection,
      json.booking_id,
      json.exam_location,
      json.exam_date,
      json.controller ? Profile.asProfile(json.controller) : null,
      json.invoice_exists,
      json.invoice_ids,
      json.list_on_boverket,
      json.case_type.value === this.CASE_TYPE_OVERTAKING && json.current_ca ? json.current_ca.value : '',
      json.case_type.value === this.CASE_TYPE_OVERTAKING && json.current_certificate_number ? json.current_certificate_number.value : '',
      json.invoiceable,
      json.id_card,
      json.is_sbr
    )
  }

  get getBoverket () {
    return this.listOnBoverket.value === '1' ? 'Yes' : 'No'
  }

  get stateCell () {
    return this.state
  }

  get caseLink () {
    return { name: 'CaseView', params: { caseId: this.id } }
  }

  get certificateNumber () {
    if (this.certificate !== null) {
      return this.certificate.certificateNumber
    }
    return null
  }
  get reviewCertificateLink () {
    if (this.isReportingCase() || this.isRenewalCase()) {
      return { name: 'ReportsUpcomingView', params: { certificateId: this.certificate.id } }
    }
    return { name: 'ReportsUpcomingView', params: { certificateId: '' } }
  }

  get isCertificateReported () {
    return this.certificate.isReported
  }

  get isCertificateReportedCell () {
    return this.isCertificateReported
  }

  get requestedAuditor () {
    return this.auditorRequests.length && this.auditorRequests[0].auditor
  }

  get invoiceState () {
    return {
      display: this.invoiced ? 'Yes' : 'No',
      level: this.invoiced ? State.SUCCESS : State.DANGER,
      custom_classes: ['tag-flex']
    }
  }

  get wantIdCard () {
    return {
      display: this.idCard ? this.idCard.value === '1' ? 'Yes' : 'No' : 'No',
      level: this.idCard ? this.idCard.value === '1' ? State.SUCCESS : State.DANGER : State.DANGER,
      custom_classes: ['tag-flex']
    }
  }

  get memberOfSbr () {
    return {
      display: this.memberOfSBR ? this.memberOfSBR.value === '1' ? 'Yes' : 'No' : 'No',
      level: this.memberOfSBR ? this.memberOfSBR.value === '1' ? State.SUCCESS : State.DANGER : State.DANGER,
      custom_classes: ['tag-flex']
    }
  }

  get invoiceCell () {
    return this.invoiceState
  }

  get hasReviewReport () {
    return this.isInReview() || this.isReviewDone() || this.isDone() || this.isReportApproved() || this.isRejected()
  }

  updateCurrentCase (_case) {
    if (this.id !== _case.id) {
      throw new Error('Objects missmatch')
    }
    this.state = _case.state
    this.caseNumber = _case.caseNumber
    this.applicationDate = _case.applicationDate
    this.applicationSignedDate = _case.applicationSignedDate
    this.qualification = _case.qualification
    this.created = _case.created
    this.modified = _case.modified
    this.responsible = _case.responsible
    this.service = _case.service
    this.reviewer = _case.reviewer
    this.customer = _case.customer
    this.progress = _case.progress
    this.certificate = _case.certificate
    this.result = _case.result
    this.auditorRequests = _case.auditorRequests
    this.caseType = _case.caseType
    this.examSelection = _case.examSelection
    this.examDate = _case.examDate
    this.controller = _case.controller
    this.invoiced = _case.invoiced
    this.listOnBoverket = _case.listOnBoverket
  }

  setDocuments (documents) {
    this.documents = documents
  }

  isOvertakingCase () {
    return this.caseType.value === Case.CASE_TYPE_OVERTAKING
  }

  isReportingCase () {
    return this.caseType.value === Case.CASE_TYPE_REPORTING
  }

  isRenewalCase () {
    return this.caseType.value === Case.CASE_TYPE_RENEWAL
  }

  isInReview () {
    return this.state.value === CaseState.IN_REVIEW
  }

  isReviewDone () {
    return this.state.value === CaseState.REVIEW_DONE
  }

  isDone () {
    return this.state.value === CaseState.CERTIFICATE_ISSUED
  }

  isRejected () {
    return this.state.value === CaseState.CASE_REJECTED
  }

  isReportApproved () {
    return this.state.value === CaseState.REPORT_APPROVED
  }

  isApplicationSigned () {
    return this.signature !== null
  }

  isCreated () {
    return [
      CaseState.CERTIFICATE_ISSUED,
      CaseState.CASE_REJECTED,
      CaseState.UNCONFIRMED,
      CaseState.CONFIRMED,
      CaseState.REVIEW_DONE,
      CaseState.REPORT_APPROVED
    ].includes(this.state.value)
  }

  isAuditorAssigned () {
    return this.state.value === CaseState.AUDITOR_ASSIGNED
  }

  isAuditorRequested () {
    return this.state.value === CaseState.AUDITOR_REQUESTED
  }

  isUserReviewer (user) {
    return this.reviewer && user.profile.id === this.reviewer.profileId && user.profile.profileType === this.reviewer.profileType
  }

  hasValidStates (states) {
    return states.includes(this.state.value)
  }

  hasPendingAuditorRequest (_authUser) {
    for (let auditorRequest of this.auditorRequests) {
      if (auditorRequest.state.value === AuditorRequest.PENDING && auditorRequest.auditor.id === _authUser.profile.id) {
        return true
      }
    }
    return false
  }

  getActionStateForCaseType (_authUser, actionState) {
    if (_authUser.isAuditor()) {
      return actionState
    }
    if (_authUser.isAdministrator()) {
      return actionState && this.caseType.value === Case.CASE_TYPE_REPORTING
    }
    return false
  }

  canClaimCase (_authUser) {
    let actionState = this.isInReview() && !this.isUserReviewer(_authUser)
    return this.getActionStateForCaseType(_authUser, actionState)
  }

  canManagerReviewCase (_authUser) {
    return this.reviewer && !_authUser.hasProfile(this.reviewer) && this.responsible.isManager()
  }

  canBeginReviewCase (_authUser) {
    let actionState = this.isAuditorAssigned(_authUser)
    return !this.isRejected() && this.getActionStateForCaseType(_authUser, actionState)
  }

  canContinueReviewCase (_authUser) {
    let actionState = this.isInReview() && this.isUserReviewer(_authUser)
    return this.getActionStateForCaseType(_authUser, actionState)
  }

  canRejectCase (_authUser) {
    if (this.isRejected()) {
      return false
    }
    if (_authUser.isAuditor()) {
      return this.isAuditorRequested() && this.hasPendingAuditorRequest(_authUser)
    }
    if (_authUser.isAdministrator()) {
      return this.isCreated() || [CaseState.READY, CaseState.AUDITOR_REQUESTED, CaseState.AUDITOR_DECLINED].includes(this.state.value)
    }
    return false
  }

  getCaseActions (_authUser, handleAction) {
    let result = []
    if (this.canRejectCase(_authUser)) {
      result.push({
        type: 'ACTION',
        func () {
          handleAction(Case.REJECT_CASE)
        },
        text: 'Deny case'
      })
    }
    if (!this.invoiced && _authUser.isAdministrator()) {
      result.push({
        type: 'ACTION',
        func () {
          handleAction(Case.SET_INVOICED)
        },
        text: 'Set as invoiced'
      })
    }
    // The following conditions are self-excluding
    if (this.isAuditorRequested() && this.hasPendingAuditorRequest(_authUser) && _authUser.isAuditor()) {
      return [
        {
          type: 'ACTION',
          func () {
            handleAction(Case.ACCEPT_CASE)
          },
          text: 'Accept case'
        }
      ].concat(result)
    }
    if (this.canBeginReviewCase(_authUser)) {
      return [
        {
          type: 'ACTION',
          func () {
            handleAction(Case.BEGIN_REVIEW)
          },
          text: 'Begin review'
        }
      ].concat(result)
    }
    if (this.canClaimCase(_authUser)) {
      return [
        {
          type: 'ACTION',
          func () {
            handleAction(Case.CLAIM_CASE)
          },
          text: 'Claim case'
        }
      ].concat(result)
    }
    if (this.responsible) {
      if (this.canContinueReviewCase(_authUser)) {
        return [
          {
            type: 'ACTION',
            func () {
              handleAction(Case.CONTINUE_REVIEW)
            },
            text: 'Continue review'
          }
        ].concat(result)
      }
      if (!this.isReportingCase() && [CaseState.READY, CaseState.AUDITOR_REQUESTED, CaseState.AUDITOR_DECLINED].includes(this.state.value) && this.responsible.isAdministrator() && _authUser.isAdministrator()) {
        return [
          {
            type: 'ROUTE',
            route: { name: 'AssignAuditorView', params: { caseId: this.id } },
            text: 'Assign an auditor'
          }
        ].concat(result)
      }
      if (this.isDone() && _authUser.isAdministrator() && this.responsible.isAdministrator()) {
        return [
          {
            type: 'ACTION',
            func () {
              handleAction(Case.CLEAR_RESPONSIBLE)
            },
            text: 'Registration done'
          }
        ].concat(result)
      }
      if (this.isReportingCase() && this.state.value === CaseState.READY && this.responsible.isAdministrator() && _authUser.isAdministrator()) {
        return [
          {
            type: 'ACTION',
            func () {
              handleAction(Case.BEGIN_REVIEW)
            },
            text: 'Begin review'
          }
        ].concat(result)
      }
    }
    return result
  }
}

class CaseState {
  static READY = 1
  static CERTIFICATE_ISSUED = 2
  static CASE_REJECTED = 3
  static UNCONFIRMED = 4
  static CONFIRMED = 5
  static AUDITOR_DECLINED = 6
  static REVIEW_DONE = 7
  static REPORT_APPROVED = 8
  static AUDITOR_REQUESTED = 9
  static IN_REVIEW = 10
  static AUDITOR_ASSIGNED = 11
  static PENDING = 12

  static VALID_STATES = [
    CaseState.READY,
    CaseState.CERTIFICATE_ISSUED,
    CaseState.CASE_REJECTED,
    CaseState.UNCONFIRMED,
    CaseState.CONFIRMED,
    CaseState.AUDITOR_DECLINED,
    CaseState.REVIEW_DONE,
    CaseState.REPORT_APPROVED,
    CaseState.AUDITOR_REQUESTED,
    CaseState.IN_REVIEW,
    CaseState.AUDITOR_ASSIGNED,
    CaseState.PENDING]

  static STATES = {
    [CaseState.READY]: {
      value: CaseState.READY,
      display: 'Ready',
      level: State.INFO
    },
    [CaseState.CERTIFICATE_ISSUED]: {
      value: CaseState.CERTIFICATE_ISSUED,
      display: 'Created',
      level: State.INFO
    },
    [CaseState.CASE_REJECTED]: {
      value: CaseState.CASE_REJECTED,
      display: 'Created',
      level: State.INFO
    },
    [CaseState.UNCONFIRMED]: {
      value: CaseState.UNCONFIRMED,
      display: 'Created',
      level: State.INFO
    },
    [CaseState.CONFIRMED]: {
      value: CaseState.CONFIRMED,
      display: 'Created',
      level: State.INFO
    },
    [CaseState.REVIEW_DONE]: {
      value: CaseState.REVIEW_DONE,
      display: 'Created',
      level: State.INFO
    },
    [CaseState.REPORT_APPROVED]: {
      value: CaseState.REPORT_APPROVED,
      display: 'Created',
      level: State.INFO
    },
    [CaseState.AUDITOR_REQUESTED]: {
      value: CaseState.AUDITOR_REQUESTED,
      display: 'Auditor requested',
      level: State.INFO
    },
    [CaseState.AUDITOR_ASSIGNED]: {
      value: CaseState.AUDITOR_ASSIGNED,
      display: 'Auditor assigned',
      level: State.SUCCESS
    },
    [CaseState.IN_REVIEW]: {
      value: CaseState.IN_REVIEW,
      display: 'In review',
      level: State.INFO
    },
    [CaseState.AUDITOR_DECLINED]: {
      value: CaseState.AUDITOR_DECLINED,
      display: 'Done',
      level: State.SUCCESS
    },
    [CaseState.PENDING]: {
      value: CaseState.PENDING,
      display: 'Pending',
      level: State.INFO
    }
  }

  constructor (value, display, level) {
    if (!CaseState.VALID_STATES.includes(value)) throw new InvalidStateError()
    this.value = value
    this.display = display
    this.level = level
  }

  static asState (json) {
    return new State(
      json.value,
      json.display,
      json.level
    )
  }

  static ongoingStates () {
    return [
      CaseState.READY,
      CaseState.CASE_REJECTED,
      CaseState.UNCONFIRMED,
      CaseState.CONFIRMED,
      CaseState.AUDITOR_DECLINED,
      CaseState.AUDITOR_REQUESTED,
      CaseState.AUDITOR_ASSIGNED,
      CaseState.IN_REVIEW,
      CaseState.REVIEW_DONE,
      CaseState.PENDING
    ]
  }
}

class CaseResult {
  constructor (caseId, comment, state) {
    this.caseId = caseId
    this.comment = comment
    this.state = state
  }

  static asCaseResult (json) {
    return new CaseResult(
      json.case,
      json.comment,
      State.asState(json.state)
    )
  }
}

class CaseType {
  constructor (display, value) {
    this.display = display
    this.value = value
  }

  static asCaseType (json) {
    return new CaseType(
      json.display,
      json.value
    )
  }
}

class Signature {
  constructor (name, pnr, signedAt, ipAddress) {
    this.name = name
    this.pnr = pnr
    this.signedAt = signedAt
    this.ipAddress = ipAddress
  }

  static asSignature (json) {
    return new Signature(
      json.name,
      json.pnr,
      json.signed_at ? dateTimeFormat(json.signed_at, 'YYYY-MM-DD HH:mm') : '',
      json.ip_address
    )
  }
}

export {
  Case,
  CaseState,
  CaseResult
}
